// Catalog.ID Client — Active Identity State
// ============================================================================
// Manages which identity is currently active (personal, delegate, or organization).
// Drives workspace visibility and data context.

import type { LinkedIdentity } from '../api/types';

export type IdentityType = 'personal' | 'delegate' | 'organization' | 'agent';

export interface ActiveIdentity {
    cid: string;
    accountType: string;
    organizationCid?: string;
    roles?: string[];
}

class IdentityState {
    private active: ActiveIdentity | null = null;
    private linked: LinkedIdentity[] = [];
    private changeCallbacks: Array<() => void> = [];

    /** Set the active identity. */
    setActive(identity: ActiveIdentity): void {
        this.active = identity;
        this.notifyChange();
    }

    /** Get the active identity. */
    getActive(): ActiveIdentity | null {
        return this.active;
    }

    /** Get the active CID. */
    getActiveCid(): string | null {
        return this.active?.cid ?? null;
    }

    /** Set the linked identities list (for context switching). */
    setLinkedIdentities(identities: LinkedIdentity[]): void {
        this.linked = identities;
    }

    /** Get linked identities. */
    getLinkedIdentities(): LinkedIdentity[] {
        return this.linked;
    }

    /** Whether the active identity is a delegate with a specific role. */
    isDelegate(): boolean {
        return this.active?.accountType === 'delegate';
    }

    /** Whether the active identity is a personal identity. */
    isPersonal(): boolean {
        return this.active?.accountType === 'natural_person';
    }

    /** Get the organization CID if active identity is a delegate. */
    getOrganizationCid(): string | null {
        return this.active?.organizationCid ?? null;
    }

    /** Get the active delegate's roles, or empty array if not a delegate. */
    getRoles(): string[] {
        return this.active?.roles ?? [];
    }

    /** Whether the active delegate has a specific role. */
    hasRole(role: string): boolean {
        return this.getRoles().includes(role);
    }

    /** Register a callback for identity changes. Returns unsubscribe function. */
    onChange(callback: () => void): () => void {
        this.changeCallbacks.push(callback);
        return () => {
            this.changeCallbacks = this.changeCallbacks.filter(cb => cb !== callback);
        };
    }

    /** Clear all state. */
    clear(): void {
        this.active = null;
        this.linked = [];
    }

    private notifyChange(): void {
        for (const cb of this.changeCallbacks) {
            cb();
        }
    }
}

export const identity = new IdentityState();
